// Day and night lighting, weather and fireflies, drawn over the town. The time and weather come from the
// shared clock (shared/time.ts), corrected by the server's clock, so everyone sees the same sky.
import { DAY_MS, hourAt, isNight, phaseOf, previousWeather, weatherAt, weatherProgress, WEATHER_MS, type SkyOverride, type Weather } from '../../../shared/time.js';

export interface Light { x: number; y: number; r: number; warm?: boolean; fire?: boolean }

// the colour the world is multiplied by through the day (r, g, b at an hour)
const KEYS: [number, [number, number, number]][] = [
  [0, [78, 92, 160]], [4.5, [92, 98, 165]], [5.5, [190, 150, 175]], [6.3, [255, 205, 175]], [7.5, [255, 255, 255]],
  [17.5, [255, 255, 255]], [18.6, [255, 214, 165]], [19.6, [225, 150, 140]], [20.6, [120, 110, 175]], [22, [82, 95, 162]], [24, [78, 92, 160]],
];
const WEATHER_TINT: Record<Weather, [number, number, number]> = { clear: [1, 1, 1], cloudy: [0.9, 0.9, 0.93], rain: [0.78, 0.8, 0.88], storm: [0.62, 0.64, 0.74], fog: [0.95, 0.95, 0.97] };

export class Sky {
  offset = 0;                       // server clock minus ours
  override: SkyOverride | null = null;
  zone = 'town';
  private map: HTMLCanvasElement = document.createElement('canvas');
  private mctx = this.map.getContext('2d')!;
  private drops: { x: number; y: number; v: number; l: number }[] = [];
  private splashes: { x: number; y: number; t: number }[] = [];
  private flies: { x: number; y: number; p: number; s: number }[] = [];
  private flash = 0; private nextFlash = 0;
  onThunder: () => void = () => {};

  now(): number { return Date.now() + this.offset; }
  setServerTime(serverNow: number, rttMs = 0): void { const est = serverNow + rttMs / 2 - Date.now(); this.offset = Math.abs(est - this.offset) > 2000 || !this.offset ? est : this.offset * 0.8 + est * 0.2; }
  hour(): number { return hourAt(this.now(), this.override); }
  night(): boolean { return isNight(this.hour()); }
  weather(): Weather { return weatherAt(this.zone, this.now(), this.override); }

  /** 0..1 strength of a weather kind right now, easing in and out over 90 seconds at a change. */
  amount(kind: Weather): number {
    if (this.override?.weather) return this.override.weather === kind ? 1 : 0;
    const cur = this.weather(), prev = previousWeather(this.zone, this.now()), into = weatherProgress(this.now()) * WEATHER_MS / 90_000;
    if (cur === kind) return prev === kind ? 1 : Math.min(1, into);
    return prev === kind ? Math.max(0, 1 - into) : 0;
  }

  /** The multiply colour for the world right now. */
  ambient(): [number, number, number] {
    const h = this.hour();
    let i = 0; while (i < KEYS.length - 2 && KEYS[i + 1][0] <= h) i++;
    const [h0, c0] = KEYS[i], [h1, c1] = KEYS[i + 1], k = (h - h0) / (h1 - h0 || 1);
    const base = c0.map((v, j) => v + (c1[j] - v) * k) as [number, number, number];
    const tint: [number, number, number] = [1, 1, 1];
    for (const w of ['cloudy', 'rain', 'storm', 'fog'] as Weather[]) { const a = this.amount(w); if (a) for (let j = 0; j < 3; j++) tint[j] *= 1 + (WEATHER_TINT[w][j] - 1) * a; }
    return base.map((v, j) => Math.round(v * tint[j])) as [number, number, number];
  }

  /** How dark it is, 0 (noon) to about 0.65 (midnight in a storm): lights glow brighter as it rises. */
  darkness(): number { const [r, g, b] = this.ambient(); return 1 - (r * 0.3 + g * 0.5 + b * 0.2) / 255; }

  /** Light and shade over the world, then weather on top. Call with the world transform still set. */
  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, VW: number, VH: number, lights: Light[], dt: number, t: number, indoor = false): void {
    if (indoor) return this.drawIndoor(ctx, camX, camY, VW, VH, lights, t);
    const dark = this.darkness(), [r, g, b] = this.ambient();
    // cloud shadows drifting over the ground
    const clouds = this.amount('cloudy') + this.amount('rain') * 0.6 + this.amount('storm');
    if (clouds > 0.05) {
      ctx.fillStyle = `rgba(30,40,60,${0.07 * Math.min(1, clouds)})`;
      for (let i = 0; i < 5; i++) {
        const cx = ((t * 6 + i * 173) % (VW + 300)) - 150 + camX, cy = camY + ((i * 97) % Math.max(1, VH)) ;
        ctx.beginPath(); ctx.ellipse(cx, cy, 90 + (i % 3) * 30, 40 + (i % 2) * 18, 0, 0, Math.PI * 2); ctx.fill();
      }
    }
    if (dark > 0.02) {
      // the light map: the ambient colour, with warm pools of light added where lamps, windows and people are
      if (this.map.width !== VW || this.map.height !== VH) { this.map.width = VW; this.map.height = VH; }
      const m = this.mctx;
      m.globalCompositeOperation = 'source-over'; m.fillStyle = `rgb(${r},${g},${b})`; m.fillRect(0, 0, VW, VH);
      m.globalCompositeOperation = 'lighter';
      const glow = Math.min(1, dark * 1.6);
      for (const L of lights) {
        // whole pixels and a steady size: a light that wobbles by a fraction of a pixel makes its edge shimmer
        const x = Math.round(L.x - camX), y = Math.round(L.y - camY), rad = L.r; if (x < -rad || y < -rad || x > VW + rad || y > VH + rad) continue;
        const gr = m.createRadialGradient(x, y, 0, x, y, rad);
        const c = L.warm ? '255,200,130' : '200,210,255';
        const k = L.fire ? 0.93 + Math.sin(t * 2.3 + L.x) * 0.05 : 1;   // only fires breathe, slowly
        gr.addColorStop(0, `rgba(${c},${0.85 * glow * k})`); gr.addColorStop(0.55, `rgba(${c},${0.32 * glow})`); gr.addColorStop(1, `rgba(${c},0)`);
        m.fillStyle = gr; m.fillRect(x - rad, y - rad, rad * 2, rad * 2);
      }
      ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(this.map, camX, camY); ctx.restore();
    }
    // fireflies on clear and cloudy nights
    const fireflies = this.night() ? 1 - this.amount('rain') - this.amount('storm') : 0;
    if (fireflies > 0.1) {
      if (this.flies.length < 26) this.flies.push({ x: camX + Math.random() * VW, y: camY + Math.random() * VH, p: Math.random() * 6, s: 0.5 + Math.random() });
      ctx.save(); ctx.globalCompositeOperation = 'lighter';
      for (const f of this.flies) {
        f.x += Math.sin(t * f.s + f.p) * 8 * dt; f.y += Math.cos(t * f.s * 0.8 + f.p) * 6 * dt;
        if (f.x < camX - 10 || f.x > camX + VW + 10 || f.y < camY - 10 || f.y > camY + VH + 10) { f.x = camX + Math.random() * VW; f.y = camY + Math.random() * VH; }
        const a = Math.max(0, Math.sin(t * 2 * f.s + f.p)) * fireflies;
        if (a < 0.05) continue;
        ctx.fillStyle = `rgba(255,240,140,${a})`; ctx.fillRect(Math.round(f.x), Math.round(f.y), 1, 1);
        ctx.fillStyle = `rgba(255,220,100,${a * 0.25})`; ctx.fillRect(Math.round(f.x) - 1, Math.round(f.y) - 1, 3, 3);
      }
      ctx.restore();
    } else this.flies.length = 0;
    // rain and storms
    const rain = Math.min(1, this.amount('rain') + this.amount('storm') * 1.4);
    const want = Math.round(rain * Math.min(260, VW * VH / 600));
    while (this.drops.length < want) this.drops.push({ x: Math.random() * (VW + 40) - 20, y: Math.random() * VH, v: 180 + Math.random() * 90, l: 3 + Math.random() * 3 });
    if (this.drops.length > want) this.drops.length = want;
    if (this.drops.length) {
      ctx.fillStyle = 'rgba(190,210,255,0.55)';
      for (const d of this.drops) {
        d.y += d.v * dt; d.x -= d.v * 0.18 * dt;
        if (d.y > VH) { if (Math.random() < 0.3) this.splashes.push({ x: d.x, y: Math.random() * VH, t: 0 }); d.y = -d.l; d.x = Math.random() * (VW + 40) - 20; }
        ctx.fillRect(Math.round(camX + d.x), Math.round(camY + d.y), 1, d.l);
      }
      ctx.fillStyle = 'rgba(210,225,255,0.5)';
      this.splashes = this.splashes.filter(s => (s.t += dt) < 0.25);
      for (const s of this.splashes) { const x = Math.round(camX + s.x), y = Math.round(camY + s.y), k = s.t < 0.12 ? 1 : 2; ctx.fillRect(x - k, y, 1, 1); ctx.fillRect(x + k, y, 1, 1); ctx.fillRect(x, y - 1, 1, 1); }
    }
    // lightning
    const storm = this.amount('storm');
    if (storm > 0.5) {
      const now = performance.now();
      if (!this.nextFlash) this.nextFlash = now + 4000 + Math.random() * 10000;
      if (now > this.nextFlash) { this.flash = 1; this.nextFlash = now + 7000 + Math.random() * 14000; setTimeout(() => this.onThunder(), 600 + Math.random() * 1400); }
    }
    if (this.flash > 0.01) { ctx.fillStyle = `rgba(235,240,255,${this.flash * 0.55})`; ctx.fillRect(camX, camY, VW, VH); this.flash *= Math.pow(0.02, dt); }
    // fog banks
    const fog = this.amount('fog');
    if (fog > 0.02) {
      for (let i = 0; i < 4; i++) {
        const fx = camX + ((t * (4 + i) + i * 151) % (VW + 260)) - 130, fy = camY + VH * (0.2 + i * 0.22);
        const gr = ctx.createRadialGradient(fx, fy, 0, fx, fy, 150);
        gr.addColorStop(0, `rgba(235,238,245,${0.42 * fog})`); gr.addColorStop(1, 'rgba(235,238,245,0)');
        ctx.fillStyle = gr; ctx.fillRect(fx - 150, fy - 150, 300, 300);
      }
      ctx.fillStyle = `rgba(230,234,242,${0.16 * fog})`; ctx.fillRect(camX, camY, VW, VH);
    }
  }

  /** Indoors: no weather, and the light is softer at night: the room is lit by its fire and lamps. */
  private drawIndoor(ctx: CanvasRenderingContext2D, camX: number, camY: number, VW: number, VH: number, lights: Light[], t: number): void {
    const dark = this.darkness() * 0.55; if (dark < 0.02) return;
    if (this.map.width !== VW || this.map.height !== VH) { this.map.width = VW; this.map.height = VH; }
    const m = this.mctx, k = 1 - dark;
    m.globalCompositeOperation = 'source-over'; m.fillStyle = `rgb(${Math.round(255 * k + 40 * dark)},${Math.round(255 * k + 20 * dark)},${Math.round(255 * k + 30 * dark)})`; m.fillRect(0, 0, VW, VH);
    m.globalCompositeOperation = 'lighter';
    for (const L of lights) {
      const x = Math.round(L.x - camX), y = Math.round(L.y - camY), rad = L.r, k = L.fire ? 0.93 + Math.sin(t * 2.3 + L.x) * 0.05 : 1;
      const gr = m.createRadialGradient(x, y, 0, x, y, rad);
      gr.addColorStop(0, `rgba(255,200,130,${0.9 * Math.min(1, dark * 2) * k})`); gr.addColorStop(1, 'rgba(255,200,130,0)');
      m.fillStyle = gr; m.fillRect(x - rad, y - rad, rad * 2, rad * 2);
    }
    ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(this.map, camX, camY); ctx.restore();
  }

  /** "☾ 9:15 pm · Rain" for the clock on screen. */
  label(): string {
    const h = this.hour(), w = this.weather(), ph = phaseOf(h);
    const icon = w === 'rain' ? '☂' : w === 'storm' ? '⚡' : w === 'fog' ? '≋' : w === 'cloudy' ? '☁' : ph === 'night' ? '☾' : '☀';
    const hh = Math.floor(h), mm = Math.floor((h - hh) * 4) * 15, h12 = ((hh + 11) % 12) + 1;
    return `${icon} ${h12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'am' : 'pm'}` + (w !== 'clear' ? ` · ${w[0].toUpperCase() + w.slice(1)}` : '');
  }
}
export { DAY_MS };
